import React, { useState, useMemo, useEffect } from "react";
import { message, Modal, Button } from "antd";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { translations } from "../../utils/Translation/staticTranslation";
import DiscountStats from "../../components/Discounts/DiscountStats";
import DiscountsTableWithFilters from "../../components/Discounts/DiscountsTableWithFilters";
import DiscountDetailView from "../../components/Discounts/DiscountDetailView";
import CreateDiscountModal, {
  coercePlanNamesList,
  dedupeTargetPlanLabels,
  normalizePlanNameKey,
  extractPlansArrayFromGetPlansResponse,
} from "../../components/Discounts/CreateDiscountModal";
import { Discounts as DiscountsApi } from "../../Apis/discountsApi";
import { getUserCookies } from "../../utils/Cookies";
import { jwtDecode } from "jwt-decode";
import {
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar";
import "../../components/Discounts/Discounts.css";

function pickItemPlanFields(item) {
  if (!item || typeof item !== "object") return { plans: [], plansArabic: [] };
  return {
    plans: item.plans ?? [],
    plansArabic: item.plans_arabic ?? item.plansArabic ?? [],
  };
}

const STATIC_STATS = {
  active: 0,
  expired: 0,
  totalRedemptions: 0,
  revenueImpact: 0,
};

const Discounts = () => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.discountsPage;
  const location = useLocation();

  const user = useSelector((state) => state.user.user);
  const token = getUserCookies()?.token;
  const decoded = token ? jwtDecode(token) : null;
  const market_role = decoded?.market_role;
  const isReadOnly =
    market_role === "2" ||
    market_role === "3" ||
    isProfileModuleReadOnly(user, "discounts");
  const canDelete = !isReadOnly && !isProfileModuleDeleteBlocked(user, "discounts");

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState(STATIC_STATS);
  const [discountsData, setDiscountsData] = useState([]);
  const [discountsTotal, setDiscountsTotal] = useState(0);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "All Status",
    planType: "All Plan Types",
    page: 1,
    pageSize: 10,
  });

  // Reset selectedDiscount when on main discounts page
  // This ensures clicking "Discounts" in sidebar always shows the list view
  useEffect(() => {
    // Always reset when pathname is exactly /discounts (main page)
    if (location.pathname === "/discounts") {
      setSelectedDiscount(null);
    }
  }, [location.pathname, location.key]);

  // Fetch discount details/stats on component mount
  useEffect(() => {
    const fetchDiscountDetails = async () => {
      try {
        setStatsLoading(true);
        const response = await DiscountsApi.getDiscountDetails();

        if (response?.data?.success && response?.data?.result) {
          const result = response.data.result;

          // Parse revenue impact - remove "SAR " prefix and extract number
          let revenueImpact = 0;
          if (result.revenuew_impact) {
            // Remove "SAR" prefix (case insensitive) and any whitespace, then remove commas
            const revenueStr = result.revenuew_impact
              .toString()
              .replace(/SAR\s*/i, "")
              .replace(/,/g, "")
              .trim();
            revenueImpact = parseFloat(revenueStr) || 0;
          }

          // Map API response to stats format
          setStats({
            active: result.active || 0,
            expired: result.expired || 0,
            totalRedemptions: result.total || 0, // Using total as totalRedemptions
            revenueImpact: revenueImpact,
          });
        }
      } catch (error) {
        console.error("Error fetching discount details:", error);
        message.error(
          t?.error_fetching_stats || "Error fetching discount statistics"
        );
      } finally {
        setStatsLoading(false);
      }
    };

    fetchDiscountDetails();
  }, [t]);

  // Fetch discounts list from API
  useEffect(() => {
    const fetchDiscountsList = async () => {
      try {
        setLoading(true);
        // Get local timezone
        const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await DiscountsApi.getDiscountsList({
          code: filters.search || "",
          status: filters.status || "",
          plan: filters.planType || "",
          page: filters.page,
          pageSize: filters.pageSize,
          localTimezone: localTimezone,
        });

        if (response?.data) {
          const d = response.data;
          const rows = Array.isArray(d.results)
            ? d.results
            : Array.isArray(d.result)
            ? d.result
            : null;
          if (!rows) {
            setDiscountsData([]);
            setDiscountsTotal(d.count ?? 0);
          } else {
          // Map API response to component format
          const mappedData = rows.map((item) => {
            // Capitalize first letter of status
            const statusCapitalized =
              item.status?.charAt(0).toUpperCase() + item.status?.slice(1) ||
              "Active";

            // Normalize discount type and value (support plan extension duration)
            const discountTypeRaw = item.discount_type || "percentage";
            const normalizedType =
              discountTypeRaw?.toString().toLowerCase().replace(/\s+/g, "_") ||
              "percentage";
            const isPlanExtension = normalizedType === "plan_extension";
            const discountValue = isPlanExtension
              ? parseInt(
                  item.extension_duration ?? item.discount_percentage ?? 0,
                  10
                )
              : parseFloat(item.discount_percentage || 0);

            // Get max redemptions (can be null)
            const maxRedemptions =
              item.max_redemptions !== null &&
              item.max_redemptions !== undefined
                ? item.max_redemptions
                : item.maximum_redemptions !== null &&
                  item.maximum_redemptions !== undefined
                ? item.maximum_redemptions
                : null;

            const { plans: rawPlans, plansArabic: rawPlansAr } =
              pickItemPlanFields(item);
            const { plans: targetPlansList, plansArabic: targetPlansArabicList } =
              dedupeTargetPlanLabels(rawPlans, rawPlansAr);

            return {
              id: item.id,
              discountCode: item.code || "",
              description: item.name || "",
              descriptionArabic: item.name_arabic || "",
              // Keep API type for logic, display mapping handled in table/detail
              type: isPlanExtension ? "plan_extension" : discountTypeRaw,
              value: discountValue,
              startDate: item.valid_from || "",
              endDate: item.valid_to || "",
              targetPlans: targetPlansList,
              targetPlansArabic: targetPlansArabicList,
              audience: item.audience_preset || "",
              audienceArabic: item.audience_preset_arabic || "",
              audianceTag: item.audiance_tag || "",
              audianceTagArabic: item.audiance_tag_arabic || "",
              used: item.total_redemptions || 0,
              total: maxRedemptions,
              status: statusCapitalized,
            };
          });

          setDiscountsData(mappedData);
          setDiscountsTotal(d.count || 0);
          }
        }
      } catch (error) {
        console.error("Error fetching discounts list:", error);
        message.error(
          t?.error_fetching_discounts || "Error fetching discounts list"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDiscountsList();
  }, [filters, t]);

  const pagination = useMemo(
    () => ({
      current: filters.page,
      pageSize: filters.pageSize,
      total: discountsTotal,
    }),
    [filters.page, filters.pageSize, discountsTotal]
  );

  const handleSearchChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }));
  };

  const handleStatusFilterChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      status: value,
      page: 1,
    }));
  };

  const handlePlanTypeFilterChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      planType: value,
      page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleView = async (record) => {
    if (!record?.id) {
      message.error(t?.discount_id_missing || "Discount ID is missing");
      return;
    }

    try {
      setDetailLoading(true);
      const response = await DiscountsApi.getDiscountById(record.id);

      if (response?.data) {
        const item = response.data;

        // Capitalize first letter of status
        const statusCapitalized =
          item.status?.charAt(0).toUpperCase() + item.status?.slice(1) ||
          "Active";

        // Normalize discount type and value (support plan extension duration)
        const discountTypeRaw = item.discount_type || "percentage";
        const normalizedType =
          discountTypeRaw?.toString().toLowerCase().replace(/\s+/g, "_") ||
          "percentage";
        const isPlanExtension = normalizedType === "plan_extension";
        const discountValue = isPlanExtension
          ? parseInt(
              item.extension_duration ?? item.discount_percentage ?? 0,
              10
            )
          : parseFloat(item.discount_percentage || 0);

        // Get total redemptions (use max_redemptions or maximum_redemptions)
        // Preserve null if max_redemptions is null
        const totalRedemptions =
          item.max_redemptions !== null && item.max_redemptions !== undefined
            ? item.max_redemptions
            : item.maximum_redemptions !== null &&
              item.maximum_redemptions !== undefined
            ? item.maximum_redemptions
            : null;

        const { plans: detailRaw, plansArabic: detailRawAr } =
          pickItemPlanFields(item);
        const { plans: detailPlans, plansArabic: detailPlansArabic } =
          dedupeTargetPlanLabels(detailRaw, detailRawAr);

        // Map API response to component format (keep API type, handle display separately)
        const mappedDiscount = {
          id: item.id,
          discountCode: item.code || "",
          description: item.name || "",
          description_arabic: item.name_arabic || "",
          type: isPlanExtension ? "plan_extension" : discountTypeRaw,
          value: discountValue,
          startDate: item.valid_from || "",
          endDate: item.valid_to || "",
          targetPlans: detailPlans,
          targetPlans_arabic: detailPlansArabic,
          audience: item.audience_preset || "",
          audience_arabic: item.audience_preset_arabic || "",
          audianceTag: item.audiance_tag || "",
          audianceTag_arabic: item.audiance_tag_arabic || "",
          used: item.total_redemptions || 0,
          total: totalRedemptions,
          status: statusCapitalized,
        };

        setSelectedDiscount(mappedDiscount);
      }
    } catch (error) {
      console.error("Error fetching discount details:", error);
      message.error(
        t?.error_fetching_discount || "Error fetching discount details"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedDiscount(null);
  };

  const handleEdit = async (record) => {
    if (isReadOnly) {
      message.warning(
        t?.read_only_action_blocked ||
          "You do not have permission to edit discounts."
      );
      return;
    }
    if (!record?.id) {
      message.error(t?.discount_id_missing || "Discount ID is missing");
      return;
    }

    try {
      setLoading(true);
      const response = await DiscountsApi.getDiscountById(record.id);

      if (response?.data) {
        const item = response.data;

        // Parse dates and times
        let startDate = null;
        let startTime = null;
        let endDate = null;
        let endTime = null;

        if (item.valid_from) {
          const validFromDate = dayjs(item.valid_from);
          startDate = validFromDate;
          startTime = validFromDate.format("HH:mm");
        }

        if (item.valid_to) {
          const validToDate = dayjs(item.valid_to);
          endDate = validToDate;
          endTime = validToDate.format("HH:mm");
        }

        // Determine discount type and value (support plan extension)
        const discountTypeRaw = item.discount_type || "percentage";
        const isPlanExtension =
          typeof discountTypeRaw === "string" &&
          discountTypeRaw.toLowerCase().replace(/\s+/g, "_") ===
            "plan_extension";
        const discountType = isPlanExtension
          ? "plan_extension"
          : discountTypeRaw;
        const discountValue = parseFloat(item.discount_percentage || 0);
        const extensionDurationRaw =
          item.extension_duration ?? item.discount_percentage ?? 1;
        const extensionDuration =
          parseInt(extensionDurationRaw, 10) > 0
            ? parseInt(extensionDurationRaw, 10)
            : 1;

        // Map plans - support multiple plans
        let applicablePlans = [];
        const { plans: rawPlansForEdit } = pickItemPlanFields(item);
        const planNamesForEdit = coercePlanNamesList(rawPlansForEdit);
        if (
          item.applies_to_all_plans ||
          planNamesForEdit.includes("All Plans")
        ) {
          // If all plans, set empty array (will show as "All Plans" selected)
          applicablePlans = [];
        } else if (planNamesForEdit.length > 0) {
          applicablePlans = dedupeTargetPlanLabels(
            planNamesForEdit.filter((plan) => plan !== "All Plans"),
            []
          ).plans;
        }

        // Map countries
        let targetCountries = [];
        if (
          item.countries &&
          Array.isArray(item.countries) &&
          item.countries.length > 0
        ) {
          // If countries exist, use them as an array
          targetCountries = item.countries;
        }

        // Map applicable type
        const applicableType = item.applicable_type || "one_time";
        const firstPurchaseOnly = applicableType === "first_purchase_only";
        const redeemMoreThanOnce = applicableType === "multiple";

        // Map audience preset
        let audience = "All Users";
        if (item.audience_preset) {
          audience = item.audience_preset;
        }

        // Capitalize first letter of status
        const statusCapitalized =
          item.status?.charAt(0).toUpperCase() + item.status?.slice(1) ||
          "Active";

        // Prepare edit data
        const editData = {
          id: item.id,
          discountNameEnglish: item.name || "",
          discountNameArabic: item.name_arabic || item.name || "",
          discountCode: item.code || "",
          discountType: discountType,
          discountPercentage:
            discountType === "percentage" ? discountValue : 0,
          discountFixed: discountType === "fixed" ? discountValue : 0,
          planExtensionMonths: isPlanExtension ? extensionDuration : 1,
          applicablePlans: applicablePlans,
          targetCountries: targetCountries,
          startDate: startDate,
          startTime: startTime,
          endDate: endDate,
          endTime: endTime,
          maxUses:
            item.max_redemptions !== null && item.max_redemptions !== undefined
              ? item.max_redemptions
              : "",
          notifyWhenMaxReached: item.notify_to_admin || false,
          firstPurchaseOnly: firstPurchaseOnly,
          redeemMoreThanOnce: redeemMoreThanOnce,
          showBanner: item.show_banner || false,
          bannerTextEnglish: item.english_text || "",
          bannerTextArabic: item.arabic_text || "",
          audience: audience,
          selectedUsers: item.recipients || [],
          status: statusCapitalized,
        };

        setEditingDiscount(editData);
        setShowCreateModal(true);
      } else {
        message.error(
          t?.error_fetching_discount || "Error fetching discount details"
        );
      }
    } catch (error) {
      console.error("Error fetching discount for edit:", error);
      message.error(
        error.response?.data?.error ||
          t?.error_fetching_discount ||
          "Error fetching discount details"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (record) => {
    if (!canDelete) {
      message.warning(
        translations[lang]?.common_error_msg?.delete_not_allowed ||
          "You do not have permission to delete discounts."
      );
      return;
    }
    if (!record?.id) {
      message.error(t?.discount_id_missing || "Discount ID is missing");
      return;
    }

    // Show confirmation modal
    setDiscountToDelete(record);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      message.warning(
        translations[lang]?.common_error_msg?.delete_not_allowed ||
          "You do not have permission to delete discounts."
      );
      setShowDeleteModal(false);
      setDiscountToDelete(null);
      return;
    }
    if (!discountToDelete?.id) {
      message.error(t?.discount_id_missing || "Discount ID is missing");
      setShowDeleteModal(false);
      setDiscountToDelete(null);
      return;
    }

    try {
      setLoading(true);
      const response = await DiscountsApi.deleteDiscount(discountToDelete.id);

      if (
        response?.status === 200 ||
        response?.status === 204 ||
        response?.data
      ) {
        message.success(
          t?.discount_deleted_successfully || "Discount deleted successfully"
        );
        // Refresh the discounts list by resetting to page 1
        setFilters((prev) => ({
          ...prev,
          page: 1,
        }));
      }
    } catch (error) {
      console.error("Error deleting discount:", error);
      message.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t?.error_deleting_discount ||
          "Error deleting discount"
      );
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDiscountToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDiscountToDelete(null);
  };

  const handleCreateDiscount = () => {
    if (isReadOnly) {
      message.warning(
        t?.read_only_action_blocked ||
          "You do not have permission to create discounts."
      );
      return;
    }
    setEditingDiscount(null);
    setShowCreateModal(true);
  };

  // Helper function to parse and display API errors
  const parseAndDisplayErrors = (errorDetails) => {
    if (!errorDetails) return;

    try {
      // The details might be a string that looks like a Python dict
      // Try to parse it as JSON first, if that fails, try to extract from string
      let parsedErrors = {};

      if (typeof errorDetails === "string") {
        // Try to parse as JSON
        try {
          parsedErrors = JSON.parse(errorDetails);
        } catch {
          // If JSON parse fails, try to extract from Python dict-like string
          // Example: "{'code': [ErrorDetail(string='discounts with this code already exists.', code='unique')], 'valid_from': [ErrorDetail(string='This field may not be null.', code='null')]}"
          const fieldMatches = errorDetails.match(
            /'(\w+)':\s*\[ErrorDetail\(string='([^']+)'/g
          );
          if (fieldMatches) {
            fieldMatches.forEach((match) => {
              const fieldMatch = match.match(
                /'(\w+)':\s*\[ErrorDetail\(string='([^']+)'/
              );
              if (fieldMatch) {
                parsedErrors[fieldMatch[1]] = fieldMatch[2];
              }
            });
          }
        }
      } else if (typeof errorDetails === "object") {
        parsedErrors = errorDetails;
      }

      // Display specific field errors
      const errorMessages = [];

      if (parsedErrors.code) {
        // Handle array of error messages (e.g., ["discounts with this code already exists."])
        let codeError;
        if (Array.isArray(parsedErrors.code)) {
          // Get first error message from array
          const firstError = parsedErrors.code[0];
          // If it's an object with string property, extract it; otherwise use the value directly
          if (
            typeof firstError === "object" &&
            firstError !== null &&
            firstError.string
          ) {
            codeError = firstError.string;
          } else {
            codeError = firstError;
          }
        } else if (
          typeof parsedErrors.code === "object" &&
          parsedErrors.code.string
        ) {
          codeError = parsedErrors.code.string;
        } else {
          codeError = parsedErrors.code;
        }

        // Convert to string if it's not already
        const codeErrorStr =
          typeof codeError === "string" ? codeError : String(codeError || "");

        // Display the actual API error message
        errorMessages.push(
          codeErrorStr || t?.error_code_invalid || "Code error"
        );
      }

      if (parsedErrors.valid_from) {
        const dateError = Array.isArray(parsedErrors.valid_from)
          ? parsedErrors.valid_from[0]?.string || parsedErrors.valid_from[0]
          : parsedErrors.valid_from;
        if (
          (typeof dateError === "string" &&
            dateError.includes("may not be null")) ||
          (dateError?.includes && dateError.includes("required"))
        ) {
          errorMessages.push(
            t?.error_start_date_required || "Start date is required."
          );
        } else {
          errorMessages.push(
            t?.error_start_date_invalid || `Start date: ${dateError}`
          );
        }
      }

      if (parsedErrors.valid_to) {
        const dateError = Array.isArray(parsedErrors.valid_to)
          ? parsedErrors.valid_to[0]?.string || parsedErrors.valid_to[0]
          : parsedErrors.valid_to;
        if (
          (typeof dateError === "string" &&
            dateError.includes("may not be null")) ||
          (dateError?.includes && dateError.includes("required"))
        ) {
          errorMessages.push(
            t?.error_end_date_required || "End date is required."
          );
        } else {
          errorMessages.push(
            t?.error_end_date_invalid || `End date: ${dateError}`
          );
        }
      }

      // Handle other field errors
      Object.keys(parsedErrors).forEach((field) => {
        if (
          field !== "code" &&
          field !== "valid_from" &&
          field !== "valid_to"
        ) {
          const fieldError = Array.isArray(parsedErrors[field])
            ? parsedErrors[field][0]?.string || parsedErrors[field][0]
            : parsedErrors[field];
          const fieldName = field
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
          errorMessages.push(`${fieldName}: ${fieldError}`);
        }
      });

      // Display all error messages
      if (errorMessages.length > 0) {
        errorMessages.forEach((msg) => {
          message.error(msg);
        });
      } else {
        // Fallback if parsing failed
        message.error(
          typeof errorDetails === "string"
            ? errorDetails
            : t?.error_creating_discount || "Error creating discount"
        );
      }
    } catch (parseError) {
      console.error("Error parsing error details:", parseError);
      message.error(
        typeof errorDetails === "string"
          ? errorDetails
          : t?.error_creating_discount || "Error creating discount"
      );
    }
  };

  const handleCreateDiscountSubmit = async (formData) => {
    if (isReadOnly) {
      message.warning(
        t?.read_only_action_blocked ||
          "You do not have permission to change discounts."
      );
      return;
    }
    try {
      setLoading(true);

      // Determine audience_preset - send display names as backend expects them
      let audiencePreset = "All Users";
      if (formData.selectedUsers && formData.selectedUsers.length > 0) {
        // If users are selected from the list, it's always Custom Users
        audiencePreset = "Custom Users";
      } else if (formData.audience) {
        // Use the audience value directly (it's already in display format)
        audiencePreset = formData.audience || "All Users";
      }

      // Map applicable_type based on checkboxes
      let applicableType = "one_time";
      if (formData.firstPurchaseOnly) {
        applicableType = "first_purchase_only";
      } else if (formData.redeemMoreThanOnce) {
        applicableType = "multiple";
      }

      // Map plans - need to fetch plans to get IDs
      let targetPlans = [];
      let appliesToAllPlans = false;

      // Handle multiple plans selection
      // Empty array means "All Plans" is selected
      const selectedPlans = Array.isArray(formData.applicablePlans)
        ? formData.applicablePlans
        : formData.applicablePlans === "All Plans" || !formData.applicablePlans
        ? []
        : [formData.applicablePlans];

      // Check if "All Plans" is selected (empty array) or "All Plans" is in the array
      if (selectedPlans.length === 0 || selectedPlans.includes("All Plans")) {
        appliesToAllPlans = true;
        targetPlans = [];
      } else {
        // Fetch plans to map plan names to IDs
        try {
          const plansResponse = await DiscountsApi.getPlans();
          const planRows = extractPlansArrayFromGetPlansResponse(
            plansResponse?.data
          );
          if (planRows.length > 0) {
            const idsByNameKey = new Map();
            for (const p of planRows) {
              const k = normalizePlanNameKey(p.name);
              if (!k) continue;
              if (!idsByNameKey.has(k)) idsByNameKey.set(k, []);
              idsByNameKey.get(k).push(p.id);
            }
            targetPlans = selectedPlans.flatMap(
              (planName) => idsByNameKey.get(normalizePlanNameKey(planName)) ?? []
            );
          }
        } catch (error) {
          console.error("Error fetching plans for mapping:", error);
          // If we can't fetch plans, we'll send empty array and let backend handle it
          targetPlans = [];
        }
      }

      // Map countries
      let countries = [];
      if (
        formData.targetCountries &&
        Array.isArray(formData.targetCountries) &&
        formData.targetCountries.length > 0
      ) {
        // If targetCountries is an array with values, use it directly
        countries = formData.targetCountries;
      } else if (
        formData.targetCountries &&
        typeof formData.targetCountries === "string" &&
        formData.targetCountries !== "All Countries"
      ) {
        // Legacy support: if it's a string (not "All Countries"), wrap it in array
        countries = [formData.targetCountries];
      }
      // If empty array or "All Countries", countries remains []

      // Combine date and time for valid_from and valid_to
      // Format: "YYYY-MM-DD HH:mm:ss" (space-separated format as shown in user's example)
      const formatDateTime = (date, time) => {
        if (!date) return null;

        // Handle dayjs object or string
        let dateObj;
        if (dayjs.isDayjs(date)) {
          dateObj = date;
        } else if (typeof date === "string") {
          dateObj = dayjs(date);
        } else {
          return null;
        }

        // Format date as YYYY-MM-DD
        const dateStr = dateObj.format("YYYY-MM-DD");

        // Handle time - could be dayjs object, string (HH:mm), or null
        let timeStr = "00:00";
        if (time) {
          if (dayjs.isDayjs(time)) {
            timeStr = time.format("HH:mm");
          } else if (typeof time === "string") {
            // Time is stored as "HH:mm" string from TimePicker
            timeStr = time;
          }
        }

        // Return format: "YYYY-MM-DD HH:mm:ss" (matching user's example: "2025-12-03 03:00:00")
        return `${dateStr} ${timeStr}:00`;
      };

      const validFrom = formatDateTime(formData.startDate, formData.startTime);
      const validTo = formatDateTime(formData.endDate, formData.endTime);

      // Get local timezone
      const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const isPlanExtension =
        typeof formData.discountType === "string" &&
        formData.discountType.toLowerCase().replace(/\s+/g, "_") ===
          "plan_extension";
      const discountTypePayload = isPlanExtension
        ? "plan_extension"
        : formData.discountType;

      // Build payload (add discount_percentage / extension_duration conditionally below)
      const payload = {
        code: formData.discountCode,
        name: formData.discountNameEnglish,
        name_arabic:
          formData.discountNameArabic || formData.discountNameEnglish,
        discount_type: discountTypePayload,
        valid_from: validFrom,
        valid_to: validTo,
        applies_to_all_plans: appliesToAllPlans,
        audience_preset: audiencePreset,
        recipients:
          formData.selectedUsers && formData.selectedUsers.length > 0
            ? formData.selectedUsers.map((user) => {
                // Handle both object format {id: X} and direct ID
                if (typeof user === "object" && user !== null) {
                  return user.id || user;
                }
                return user;
              })
            : [],
        countries: countries,
        applies_to_all_users: audiencePreset === "All Users",
        max_redemptions:
          formData.maxUses && formData.maxUses !== "" && formData.maxUses > 0
            ? formData.maxUses
            : null,
        notify_to_admin: formData.notifyWhenMaxReached || false,
        applicable_type: applicableType,
        target_plans: appliesToAllPlans ? [] : targetPlans,
        local_timezone: localTimezone,
        show_banner:
          formData.show_banner ?? formData.showBanner ?? false,
        english_text:
          formData.english_text ||
          formData.bannerTextEnglish ||
          formData.banner_text_english ||
          "",
        arabic_text:
          formData.arabic_text ||
          formData.bannerTextArabic ||
          formData.banner_text_arabic ||
          "",
      };

      // Attach amount fields based on type
      if (isPlanExtension) {
        payload.extension_duration = formData.planExtensionMonths
          ? parseInt(formData.planExtensionMonths, 10)
          : null;
        // Do NOT send discount_percentage for plan extension
      } else {
        payload.discount_percentage =
          formData.discountType === "percentage"
            ? formData.discountPercentage
            : formData.discountFixed;
        // Remove extension_duration when not a plan extension
        delete payload.extension_duration;
      }

      // Check if we're editing or creating
      const isEditing = editingDiscount && editingDiscount.id;
      let response;

      if (isEditing) {
        // Update existing discount
        response = await DiscountsApi.updateDiscount(
          editingDiscount.id,
          payload
        );
      } else {
        // Create new discount
        response = await DiscountsApi.createDiscount(payload);
      }

      if (
        response?.data?.success ||
        response?.status === 200 ||
        response?.status === 201
      ) {
        // If updating, call the get_user_list API
        if (isEditing && editingDiscount?.id) {
          try {
            await DiscountsApi.getUserList(editingDiscount.id);
          } catch (error) {
            console.error("Error fetching user list after update:", error);
            // Don't show error to user, just log it
          }
        }

        message.success(
          isEditing
            ? t?.discount_updated_successfully ||
                "Discount updated successfully"
            : t?.discount_created_successfully ||
                "Discount created successfully"
        );
        setShowCreateModal(false);
        setEditingDiscount(null);
        // Trigger a refetch by resetting filters to page 1
        setFilters((prev) => ({
          ...prev,
          page: 1,
        }));
      } else if (response?.data?.success === false) {
        // Handle API error response
        const errorDetails = response.data.details;
        parseAndDisplayErrors(errorDetails);
      }
    } catch (error) {
      console.error("Error creating discount:", error);

      // Handle error response
      if (error.response?.data) {
        const errorData = error.response.data;

        // Check if it's the API error format with details
        if (errorData.success === false && errorData.details) {
          parseAndDisplayErrors(errorData.details);
        }
        // Check if error data is directly an object with field errors (e.g., {"code": ["error message"]})
        else if (
          typeof errorData === "object" &&
          !errorData.success &&
          !errorData.message &&
          !errorData.error
        ) {
          // This is likely a validation error object like {"code": ["discounts with this code already exists."]}
          parseAndDisplayErrors(errorData);
        } else {
          // Fallback to generic error message
          message.error(
            errorData.message ||
              errorData.error ||
              t?.error_creating_discount ||
              "Error creating discount"
          );
        }
      } else {
        message.error(t?.error_creating_discount || "Error creating discount");
      }
    } finally {
      setLoading(false);
    }
  };

  // If a discount is selected, show the detail view
  if (selectedDiscount) {
    return (
      <DiscountDetailView
        discount={selectedDiscount}
        onBack={handleBackToList}
        loading={detailLoading}
      />
    );
  }

  return (
    <div className="discounts-page-container">
      {/* Page Title */}
      <h1 className="discounts-page-title">{t?.discounts || "Discounts"}</h1>

      {/* Summary Cards */}
      <DiscountStats stats={stats} loading={statsLoading} />

      {/* Table with Filters */}
      <DiscountsTableWithFilters
        data={discountsData}
        loading={loading}
        isReadOnly={isReadOnly}
        canDelete={canDelete}
        searchValue={filters.search}
        statusFilter={filters.status}
        planTypeFilter={filters.planType}
        pagination={pagination}
        onSearchChange={handleSearchChange}
        onStatusFilterChange={handleStatusFilterChange}
        onPlanTypeFilterChange={handlePlanTypeFilterChange}
        onPageChange={handlePageChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreateDiscount={handleCreateDiscount}
      />

      {/* Create/Edit Discount Modal */}
      <CreateDiscountModal
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingDiscount(null);
        }}
        onSubmit={handleCreateDiscountSubmit}
        editData={editingDiscount}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onCancel={handleCancelDelete}
        footer={null}
        width={500}
        centered
        className="confirm-discount-modal"
        closeIcon={null}
      >
        <div className="confirm-discount-content">
          <div className="confirm-discount-icon">
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: "#FEF3F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#F04438"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
          <h2 className="confirm-discount-title">
            {t?.modal?.confirm_delete_discount || "Confirm Delete Discount"}
          </h2>
          <p className="confirm-discount-message">
            {t?.modal?.confirm_delete_discount_message ||
              "Are you sure you want to delete this discount? This action cannot be undone."}
          </p>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "32px",
            }}
          >
            <Button
              onClick={handleCancelDelete}
              className="create-discount-modal-button"
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FFFFFF",
                color: "#344054",
              }}
            >
              {t?.modal?.cancel || "Cancel"}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              type="primary"
              className="create-discount-modal-button-primary"
              style={{
                minWidth: "120px",
                height: "46px",
                borderRadius: "50px",
              }}
            >
              {t?.modal?.delete || "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Discounts;
